import React, {useState} from 'react';

import {Dropdown, Row} from 'react-bootstrap';
import {FormModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import {Tooltip} from '@/components/tooltips';
import {Button} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import PaginationComponent from '@/components/pagination/PaginationComponent';
import Col from 'react-bootstrap/Col';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import LoadingWrapper from '@/components/loading/LoadingWrapper';
import MyNotificationsSettingsForm from './MyNotificationsSettingsForm';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import TableComponent from '@/components/table/Table';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import usePaginatedData from '@/utils/usePaginatedData';
import Form from 'react-bootstrap/Form';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface Notification {
    id: number;
    title: string;
    createdBy: {
        userDisplayName: string;
    };
    schedule: string;
    storyboardStoryInfo: string;
    active: boolean;
}

interface MyNotificationsStore {
    myNotifications: {
        deleteOne: (id: number) => void;
        setReload: (value: boolean) => void;
    };
}


const setSearchQueryState = async (
    setSearchQuery: (value: string) => void,
    value: string,
    setPage: (page: number) => void
): Promise<void> => {
    setPage(1);
    setSearchQuery(value);
};

const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);

const MyNotificationsListView: React.FC = React.memo(() => {
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [activeNotificationId, setActiveNotificationId] = useState<number | null>(null);
    
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{id: number, title: string} | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const {
        data: notifications,
        loading,
        currentPage,
        setCurrentPage,
        pages,
    } = usePaginatedData(window.URLS.myNotificationsResource, {
        storeResolver: 'myNotifications',
        requestParams: {
            params: {
                query: searchQuery,
            },
        },
    });

    const userCanAccessMyNotificationResourceView = hasPermission('fs.user_notification.list', userData?.permissions, true);
    const canCreateNotification = hasPermission('fs.user_notification.create', userData?.permissions, true);
    const canDeleteNotification = hasPermission('fs.user_notification.delete', userData?.permissions, true);

    const deleteNotification = useStoreActions<MyNotificationsStore>((actions) => actions.myNotifications.deleteOne);
    const setReloadNotifications = useStoreActions<MyNotificationsStore>((actions) => actions.myNotifications.setReload);
    
    const inputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value, setCurrentPage);
    };

    const openSettingsModal = (id?: number): void => {
        setActiveNotificationId(id || null);
        setSettingsModalOpen(true);
    };

    const closeSettingsModal = (): void => {
        setActiveNotificationId(null);
        setSettingsModalOpen(false);
    };

    const openDeleteConfirm = (id: number, title: string): void => {
        setItemToDelete({id, title});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete) {return;}
        
        setIsDeleting(true);
        try {
            const url = `${window.URLS.myNotificationsResource}/${itemToDelete.id}`;
            await axios.delete(url);
            deleteNotification(itemToDelete.id);
            setReloadNotifications(true);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const deleteOne = (id: number, title: string): void => {
        openDeleteConfirm(id, title);
    };


    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (loadingUserData) {
        return <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>;
    }


    if (!userCanAccessMyNotificationResourceView) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_email_reports.text')}/>
                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const columns = [
        {
            accessorKey: 'title',
            header: i18n.t('th_title.text'),
        },
        {
            accessorKey: 'createdBy.userDisplayName',
            header: i18n.t('th_configured.text'),
        },
        {
            accessorKey: 'schedule',
            header: i18n.t('th_schedule.text'),
        },
        {
            accessorKey: 'storyboardStoryInfo',
            header: i18n.t('th_storyboard.text'),
        },
        {
            accessorKey: 'active',
            header: i18n.t('th_active.text'),
            cell: (cell: any) => {
                if (cell.getValue() === true) {
                    return (<Icon name="mdi-check" className="text-success"/>);
                } else {
                    return (<Icon name="mdi-close" className="text-danger"/>);
                }
            },
        },
        {
            header: i18n.t('th_action.text'),
            cell: (cell: any) => (
                <>
                    <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                        <Dropdown className="d-inline-block">
                            <Dropdown.Toggle
                                as={Button}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-dots-horizontal"
                                className="dropdown-toggle-no-caret"
                                id="settings-dropdown-1"
                                data-cy="my-notifications-list-outlineinfo-btn-1"
                            />

                            <Dropdown.Menu align="end">
                                <Dropdown.Item
                                    onClick={() => openSettingsModal(cell.row.original.id)}
                                >
                                    <Icon name="mdi-settings" className="mr-2"/> {i18n.t('jt_settings.text')}
                                </Dropdown.Item>

                                {
                                    canDeleteNotification ? (
                                        <Dropdown.Item
                                            onClick={() => deleteOne(cell.row.original.id, cell.row.original.title)}
                                        >
                                            <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                        </Dropdown.Item>
                                    ) : (
                                        <Tooltip
                                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                            placement="top"
                                        >
                                            <Dropdown.Item
                                                disabled
                                                data-cy="my-notifications-list-delete-btn">
                                                <Icon name="mdi-delete-outline" className="mr-2" /> {i18n.t('jt_delete.text')}
                                            </Dropdown.Item>
                                        </Tooltip>
                                    )
                                }
                            </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_email_reports.text')}/>
            <Row>
                <Col lg={3}>
                    <Form
                        className="mb-2 search-box"
                        onSubmit={(e: React.FormEvent) => {
                            e.preventDefault();
                        }}
                    >
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => inputChange(event)}
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    {canCreateNotification ? (
                        <Button
                            variant="info"
                            size="sm"
                            className="mb-2"
                            icon="mdi-plus"
                            onClick={() => openSettingsModal()}
                            data-cy="new-notification-btn"
                        >
                            {i18n.t('th_add_new_email_report.text')}
                        </Button>
                    ) : (
                        <Tooltip
                            content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                            placement="top"
                        >
                            <div style={{cursor: 'not-allowed', display: 'flex', alignItems: 'center'}}>
                                <Button
                                    variant="secondary"
                                    size="xs"
                                    icon="mdi-plus"
                                    disabled
                                    style={{pointerEvents: 'none'}}
                                    data-cy="my-notifications-list-plus-btn">
                                    {i18n.t('th_add_new_email_report.text')}
                                </Button>
                            </div>
                        </Tooltip>
                    )}
                </Col>
            </Row>

            {loading ? (
                <LoadingWrapper loading={true} loadingText={i18n.t('jt_loading_data_general.text')} minHeight="400px"><div /></LoadingWrapper>
            ) : (
                <>
                    <div className="card">
                        <div className="card-body">
                            <TableComponent data={notifications} columns={columns} />
                            <PaginationComponent
                                pages={pages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>

                    {settingsModalOpen ? (
                        <FormModal
                            isOpen={settingsModalOpen}
                            onClose={closeSettingsModal}
                            title={i18n.t('jt_email_report.text')}
                        >
                            <MyNotificationsSettingsForm
                                notificationId={activeNotificationId || undefined}
                                userId={userData?.id || 0}
                                userName={userData?.userDisplayName || ''}
                                onDone={closeSettingsModal}
                            />
                        </FormModal>
                    ) : null}

                    <ModalAlert
                        isOpen={deleteConfirmOpen}
                        onClose={closeDeleteConfirm}
                        variant="error"
                        title={i18n.t('jt_confirm_deletion.text')}
                        message={itemToDelete ? i18n.t('es_are_you_sure_delete_notification.text', {title: itemToDelete.title}) : ''}
                        actions={[
                            {
                                label: i18n.t('jt_delete.text'),
                                onClick: confirmDelete,
                                variant: 'danger',
                                loading: isDeleting
                            }
                        ]}
                        dismissible={!isDeleting}
                    />
                </>
            )}
        </>
    );
});

export default MyNotificationsListView; 